// src/utils/api.js
import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  timeout: 20000,
  withCredentials: true,
});

// refresh 전용 인스턴스 (인터셉터 영향 최소화용)
const refreshApi = axios.create({
  baseURL: "/api",
  timeout: 10000,
  withCredentials: true,
});

let accessToken = localStorage.getItem("accessToken") || null;
let isHardLogoutInProgress = false;

// ---- 토큰 세팅 유틸 ----
export const setAccessToken = (t) => {
  accessToken = t;

  if (t) {
    api.defaults.headers.common.Authorization = `Bearer ${t}`;
    // 모든 탭에 토큰 변경 브로드캐스트
    try {
      localStorage.setItem("accessToken", t);
    } catch (e) {}
  } else {
    delete api.defaults.headers.common.Authorization;
    try {
      localStorage.removeItem("accessToken");
    } catch (e) {}
  }

  try {
    // 같은 탭 내에서 쓰는 커스텀 이벤트
    window.dispatchEvent(new Event("access-token-updated"));
  } catch (e) {}
};

if (accessToken) {
  setAccessToken(accessToken);
}

// ---- 멀티 탭 동기화 (login / logout / refresh 공통) ----
try {
  window.addEventListener("storage", (e) => {
    if (e.key === "accessToken") {
      // 다른 탭에서 access 토큰이 변경된 경우
      const newToken = e.newValue;
      if (newToken) {
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        accessToken = newToken;
      } else {
        delete api.defaults.headers.common.Authorization;
        accessToken = null;
      }
    } else if (e.key === "global-logout" && e.newValue) {
      // 다른 탭에서 로그아웃을 수행한 경우
      accessToken = null;
      delete api.defaults.headers.common.Authorization;
      // 이 탭도 바로 로그인 페이지로 이동
      window.location.replace("/login");
    }
  });
} catch (e) {}

// ---- 공통 강제 로그아웃 (401 기반, 서버 호출 없음) ----
const hardLogout = ({ callServer = false, showAlert = true } = {}) => {
  if (isHardLogoutInProgress) return;
  isHardLogoutInProgress = true;

  try {
    if (showAlert) {
      alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
    }
  } catch (e) {}

  // 자동 로그아웃에서는 굳이 서버에 /logout 다시 칠 필요 없음
  if (callServer) {
    try {
      api.post("/logout").catch(() => {});
    } catch (e) {}
  }

  accessToken = null;
  delete api.defaults.headers.common.Authorization;

  try {
    localStorage.removeItem("accessToken");
  } catch (e) {}

  // 모든 탭에 "로그아웃 발생" 브로드캐스트
  try {
    localStorage.setItem("global-logout", String(Date.now()));
  } catch (e) {}

  window.location.replace("/login");
};

// ---- refresh 중복 방지 (여러 요청/탭 동시 처리 - 탭 내) ----
let refreshingPromise = null;
const refreshWaiters = [];

const callRefresh = async () => {
  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      console.log("[REFRESH] start, current accessToken =", accessToken);

      try {
        const resp = await refreshApi.post("/refresh", null, {
          withCredentials: true,
        });

        console.log("[REFRESH] success", {
          status: resp.status,
          data: resp.data,
        });

        const newAccess = resp.data?.accessToken;
        if (!newAccess) {
          const err = new Error("NO_ACCESS_FROM_REFRESH");
          err.isRefreshError = true;
          err.refreshStatus = resp.status;
          err.refreshReason = "NO_ACCESS_FROM_REFRESH";
          console.log("[REFRESH] error: 200 but no accessToken");
          throw err;
        }

        setAccessToken(newAccess);
        return newAccess;
      } catch (err) {
        const status = err.response?.status;
        const raw = err.response?.data;
        const headers = err.response?.headers;

        console.log("[REFRESH] error", {
          status,
          raw,
          rawType: typeof raw,
          headers,
        });

        // 그냥 정보만 싣고, 여기서는 절대 hardLogout 안 함
        err.isRefreshError = true;
        err.refreshStatus = status;
        err.refreshRaw = raw;
        err.refreshHeaders = headers;

        throw err;
      } finally {
        console.log("[REFRESH] finally, clearing promise");
        refreshWaiters.splice(0).forEach((resolve) => resolve());
        refreshingPromise = null;
      }
    })();
  } else {
    await new Promise((resolve) => refreshWaiters.push(resolve));
  }

  return accessToken;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error || {};
    if (!response || !config) return Promise.reject(error);

    const status = response.status;
    const url = config.url || "";
    const isLogin = url.includes("/login");
    const isRefresh = url.includes("/refresh");
    const isLogout = url.includes("/logout");

    if (isRefresh) {
      // /refresh 자체의 응답은 여기서 별도 처리 안 함
      return Promise.reject(error);
    }

    if (status === 401) {
      if (isLogin) {
        return Promise.reject(error);
      }

      if (!config._retry && !isLogout) {
        config._retry = true;

        try {
          const newToken = await callRefresh();

          if (newToken) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${newToken}`;
          }

          return api(config);
        } catch (refreshErr) {
          // 🔎 여기서만 "같은 브라우저 레이스 vs 진짜 만료"를 판별
          const refreshStatus =
            refreshErr.refreshStatus ?? refreshErr.response?.status;

          const stored = (() => {
            try {
              return localStorage.getItem("accessToken");
            } catch {
              return null;
            }
          })();

          console.log("[INTERCEPTOR] refresh failed", {
            refreshStatus,
            stored,
            memoryAccessToken: accessToken,
          });

          if (refreshStatus === 401) {
            // ✅ 401 이면서, localStorage 에 "다른 토큰"이 있으면
            // → 같은 브라우저의 다른 탭이 이미 새 토큰을 받아둔 것으로 간주하고 살린다.
            if (stored && stored !== accessToken) {
              console.log(
                "[INTERCEPTOR] same-browser race detected, adopt stored token"
              );
              setAccessToken(stored);
              config.headers = config.headers || {};
              config.headers.Authorization = `Bearer ${stored}`;
              return api(config);
            }

            // ❌ stored 가 없거나, stored === accessToken 이면
            // → 이 브라우저에서는 새 토큰이 전혀 없는 상태 = 진짜 만료 or 다른 브라우저 재로그인
            console.log(
              "[INTERCEPTOR] 401 refresh & no newer token -> hardLogout"
            );
            hardLogout({ callServer: false });
            return Promise.reject(refreshErr);
          }

          // 네트워크 에러, 5xx 등도 여기서 한 번에 처리하고 싶으면:
          console.log("[INTERCEPTOR] non-401 refresh failure -> hardLogout");
          hardLogout({ callServer: false });
          return Promise.reject(refreshErr);
        }
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);


// ---- 명시적 로그아웃 호출 (사용자가 메뉴에서 누르는 로그아웃) ----
export const logout = async () => {
  try {
    await api.post("/logout");
  } catch (e) {
    // 서버 통신 실패해도 클라이언트 쪽 세션은 정리
  }
  // 수동 로그아웃은 서버에 이미 /logout 전송했으므로 callServer=false
  hardLogout({ callServer: false, showAlert: false });
};

export default api;
